import React, { useState, useEffect, useRef } from "react";
import { FiChevronDown } from "react-icons/fi";
import { Mail } from "lucide-react";
import { FaFilePdf, FaRegFilePdf } from "react-icons/fa";
import { RiFileExcel2Line } from "react-icons/ri";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useNavigate } from 'react-router-dom';
import { useGetGroupsQuery, useLazyPreviewPolicyReportQuery, usePolicyReportList } from "../../../../../services/redux/api/components";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import ReportLoader from '../LifePolicyRegister/ReportLoader';


/* Tailwind tokens (ensure you have brandRed in tailwind.config.js) */
const fieldBase =
  "rounded-md border border-gray-200 px-3 py-2 shadow-sm focus:outline-none focus:ring-1 focus:ring-sky-200 ";
const labelBase = "  text-gray-700";
const sectionHeader = "bg-gray-100 p-3 rounded-md  font-semibold text-gray-600";
const btnPrimary =
  "bg-brandRed hover:bg-red-600 text-white  px-6 py-2 rounded-md shadow-md transition";

/* outside click hook for dropdown */
function useOutsideClick(ref, handler) {
  useEffect(() => {
    const onClick = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) handler();
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [ref, handler]);
}

/**
 * Normalize mutation/query hook shapes.
 */
function normalizeHook(hook) {
  let trigger;
  let result = {};

  if (Array.isArray(hook)) {
    trigger = hook[0];
    result = hook[1] ?? {};
  } else if (hook && typeof hook === 'object') {
    trigger = hook.trigger ?? hook.mutate ?? hook;
    result = {
      data: hook.data,
      isLoading: hook.isLoading ?? hook.isFetching ?? hook.status === 'pending',
      error: hook.error,
      status: hook.status,
    };
  } else {
    trigger = undefined;
    result = {};
  }

  return { trigger, result };
}

/* Compact multi-select (fixed height to align with selects) */
function MultiSelectInline({ options = [], value = [], onChange, placeholder = "Select groups..." }) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const rootRef = useRef(null);
  useOutsideClick(rootRef, () => setOpen(false));

  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  };

  const filteredOptions = options.filter((o) =>
    o.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div ref={rootRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className={`${fieldBase} min-h-[40px] py-2 w-full flex items-center gap-2 justify-between bg-white`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 flex-1 flex-wrap max-h-24 overflow-y-auto">
          {value.length === 0 ? (
            <span className="text-gray-400  truncate">{placeholder}</span>
          ) : (
            <div className="flex items-center gap-1 flex-wrap">
              {value.map((id) => {
                const o = options.find((x) => x.id === id);
                return (
                  <span
                    key={id}
                    className=" bg-gray-100 px-2 py-0.5 rounded-md text-gray-700 whitespace-nowrap text-sm"
                  >
                    {o?.label ?? id}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <span className=" text-gray-500 text-sm">{value.length > 0 ? `${value.length} selected` : ""}</span>
          <FiChevronDown className="text-gray-500" />
        </div>
      </button>

      {open && (
        <div className="absolute z-30 mt-2 w-full bg-white border border-gray-200 shadow-lg rounded-md overflow-hidden">
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              placeholder="Search groups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="max-h-52 overflow-auto p-2">
            {filteredOptions.length === 0 ? (
              <div className="text-gray-400 text-center py-2">No groups found</div>
            ) : (
              filteredOptions.map((o) => (
                <label key={o.id} className="flex items-center gap-3 px-2 py-2 hover:bg-gray-50 rounded-md cursor-pointer">
                  <input type="checkbox" checked={value.includes(o.id)} onChange={() => toggle(o.id)} className="w-4 h-4" />
                  <span className="">{o.label}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PremiumOutstandingReport() {
  const navigate = useNavigate();
  const { data: groupsResult, isFetching: groupsLoading } = useGetGroupsQuery({ pageNumber: 1, pageSize: 1000 });
  
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfObjectUrl, setPdfObjectUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [excelPayload, setExcelPayload] = useState(null);

  // RTK Query hooks
  const [triggerPreview, previewResult] = useLazyPreviewPolicyReportQuery();
  
  // RTK Query for Excel export data
  const { data: excelData, isLoading: excelLoading, isError: excelError } = usePolicyReportList(
    1, 
    10000, 
    excelPayload || {}, 
    { skip: !excelPayload }
  );

  // Formik will manage form state via initialValues
  const initialValues = {
    fromDate: "",
    toDate: "",
    paymentMode: "",
    policyStatus: "",
    groupsSelected: [],
    sortBy: "group",
    reportFields: { address: false, mobile: false, email: false },
  };

 const groupsOptions = (
    groupsResult?.data?.map((g) => ({ id: String(g.GroupId ?? g.id ?? ''), label: g.GroupName ?? g.GroupCode ?? String(g.GroupID ?? g.id ?? '') })) || []
  );

  const buildPayload = (values) => {
    const payload = {
      fromDate: values.fromDate || null,
      toDate: values.toDate || null,
      groupIds: values.groupsSelected && values.groupsSelected.length ? values.groupsSelected.join(',') : null,
      sortBy: values.sortBy || 'groupwise',
      includeAddress: values.reportFields?.address ? '1' : '0',
      includeMobile: values.reportFields?.mobile ? '1' : '0',
      includeEmail: values.reportFields?.email ? '1' : '0',
      includeLapsed: '0',
      filterByDate: "fup",
      reportTitle: 'Premium Outstanding Report',
      sortingOrder: values.sortBy || 'groupwise'
    };
    console.log('Premium Outstanding Payload:', payload);
    return payload;
  };

  // Cleanup effect
  React.useEffect(() => () => { if (pdfObjectUrl) URL.revokeObjectURL(pdfObjectUrl); }, [pdfObjectUrl]);

  const handlePdfPreview = async (values) => {
    try {
      setIsGenerating(true);
      const payload = buildPayload(values);
      console.log('PDF Preview - Form Values:', values);
      console.log('PDF Preview - Payload:', payload);
      
      // Validate date range for Premium Outstanding Report
      if (!payload.fromDate || !payload.toDate) {
        alert('Please select both From Date and To Date to generate the Premium Outstanding Report.');
        setIsGenerating(false);
        return;
      }
      
      console.log('Triggering PDF preview with payload:', payload);
      const result = await triggerPreview(payload).unwrap();
      console.log('PDF Preview Result:', result);
      
      if (result?.pdf) {
        const byteCharacters = atob(result.pdf);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
        const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
        setPdfObjectUrl(URL.createObjectURL(blob));
        setShowPdfModal(true);
      }
    } catch (error) {
      console.error('PDF preview error:', error);
      if (error.status === 404) {
        alert('No data found for the selected filters. Please adjust your filter criteria and try again.');
      } else {
        alert('Failed to generate PDF preview.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // Effect to handle Excel export when data is ready
  React.useEffect(() => {
    if (!excelPayload || excelLoading || excelError) return;
    if (!excelData) return;
    
    const rows = Array.isArray(excelData) ? excelData : (excelData?.data ?? []);
    
    if (!rows.length) {
      alert('No data available for the selected filters.');
      setIsGenerating(false);
      setExcelPayload(null);
      return;
    }
    
    const generateExcel = async () => {
      try {
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Premium Outstanding Report');
        
        const includeAddress = excelPayload.includeAddress === '1';
        const includeMobile = excelPayload.includeMobile === '1';
        const includeEmail = excelPayload.includeEmail === '1';
        
        const columns = [
          { header: 'Group Code', key: 'GroupCode', width: 20 }, 
          { header: 'Area', key: 'Area', width: 25 }, 
          { header: 'Life Assured', key: 'LifeAssured', width: 25 }, 
          { header: 'Policy No', key: 'PolicyNo', width: 20 }, 
          { header: 'Policy Status', key: 'PolicyStatus', width: 20 },
          { header: 'Product', key: 'Product', width: 25 }, 
          { header: 'Term/PPT', key: 'TermPPT', width: 12 }, 
          { header: 'Commencement Date', key: 'CommencementDate', width: 18 }, 
          { header: 'FUP Date', key: 'FUPDate', width: 18 },
          { header: 'Sum Assured', key: 'SumAssured', width: 15 }, 
          { header: 'Payment Mode', key: 'PaymentMode', width: 15 }, 
          { header: 'Net Premium', key: 'NetPremium', width: 15 }, 
          { header: 'Agency', key: 'Agency', width: 20 }
        ];
        
        if (includeAddress) columns.push({ header: 'Address', key: 'Address', width: 30 });
        if (includeMobile) columns.push({ header: 'Mobile', key: 'Mobile', width: 15 });
        if (includeEmail) columns.push({ header: 'Email', key: 'Email', width: 25 });
        
        ws.columns = columns;
        ws.getRow(1).font = { bold: true, size: 12 };
        ws.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2CC' } };
        
        rows.forEach(row => {
          const areaValue = (row.raw && row.raw.AreaName) ?? row.AreaName ?? row.Area ?? '';
          ws.addRow({ 
            GroupCode: row.GroupCode || '', 
            Area: areaValue, 
            LifeAssured: row.LifeAssured || '', 
            PolicyNo: row.PolicyNo || '', 
            PolicyStatus: row.PolicyStatus || row.Policy_Status || '',
            Product: row.Product || '', 
            TermPPT: row.TermPPT || '', 
            CommencementDate: row.CommDate || row.CommencementDate || '', 
            FUPDate: row.FUPDate || row.fupDate || '',
            SumAssured: row.SumAssured || '', 
            PaymentMode: row.PaymentMode || '', 
            NetPremium: row.NetPremium || '', 
            Agency: row.Agency || '', 
            Address: row.Address || '', 
            Mobile: row.Mobile || '', 
            Email: row.Email || '' 
          });
        });
        
        saveAs(new Blob([await wb.xlsx.writeBuffer()], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `Premium_Outstanding_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
      } catch (error) {
        console.error('Excel generation error:', error);
        alert('Failed to generate Excel file.');
      } finally {
        setIsGenerating(false);
        setExcelPayload(null);
      }
    };
    
    generateExcel();
  }, [excelData, excelPayload, excelLoading, excelError]);

  const handleExcelExport = (values) => {
    setIsGenerating(true);
    const payload = buildPayload(values);
    console.log('Excel Export - Form Values:', values);
    console.log('Excel Export - Payload:', payload);
    
    // Validate date range
    if (!payload.fromDate || !payload.toDate) {
      alert('Please select both From Date and To Date to generate the Premium Outstanding Report.');
      setIsGenerating(false);
      return;
    }
    
    console.log('Setting Excel payload:', payload);
    setExcelPayload(payload);
  };  const handleGenerate = (values) => {
    const payload = buildPayload(values);
    
    // Validate date range
    if (!payload.fromDate || !payload.toDate) {
      alert('Please select both From Date and To Date to generate the Premium Outstanding Report.');
      return;
    }

    console.log("Navigate payload:", payload);
    navigate('/premium-outstanding-report-preview', { state: { payload } });
  };

  return (
    <div className="w-full p-4">
      <div style={{ boxShadow: "0 0 8px #ccc" }} className="w-full rounded bg-white">
        <Formik initialValues={initialValues} onSubmit={handleGenerate}>
          {({ values, setFieldValue }) => (
            <Form className="w-full">
              <div className="p-2 border-b border-brandRed">
                <div className="flex justify-between items-center">
                  <h5 className="text-brandRed font-bold m-3">Premium Outstanding Report</h5>
                  <div className="flex gap-3 items-center">
                    <Mail size={38} className="text-brandRed cursor-not-allowed opacity-50" />
                    <RiFileExcel2Line size={38} className="text-green-700 cursor-pointer hover:scale-110 transition" onClick={() => handleExcelExport(values)} title="Download Excel" />
                    <FaFilePdf size={37} className="text-red-600 cursor-pointer hover:scale-110 transition" onClick={() => handlePdfPreview(values)} title="Preview PDF" />
                  </div>
                </div>
              </div>

              <div className="p-6">
                {/* ===== FILTER SELECTION SECTION ===== */}
                {/* ===== FILTER SELECTION SECTION ===== */}
                {/* ===== FILTER SELECTION SECTION ===== */}
                <h6 className="font-bold mb-3 my-3 bg-gray-100 rounded p-2 text-gray-700">
                  Filter Selection
                </h6>

                <div className="grid grid-cols-12 items-center p-3">
                  <div className="col-span-12 lg:col-span-2">
                    <label className="text-gray-700 font-medium">FUP Date Period</label>
                  </div>

                  <div className="col-span-12 lg:col-span-10 flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-700">From</span>
                      <Field
                        type="date"
                        name="fromDate"
                        className="rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
                        style={{ width: "280px" }}
                        aria-label="From date"
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-gray-700">To</span>
                      <Field
                        type="date"
                        name="toDate"
                        className="rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
                        style={{ width: "280px" }}
                        aria-label="To date"
                      />
                    </div>
                  </div>
                </div>


                {/* ===== end combined block ===== */}

                {/* GROUPS row */}

                {/* Sorting order */}
                <h6 className="text-gray-700 font-bold mb-3 my-3 bg-gray-100 rounded p-2 ">Sorting Order</h6>
                <div className="flex flex-wrap items-center gap-6  p-3">
                  <label className="flex items-center gap-2">
                    <Field type="radio" name="sortBy" value="group" className="w-4 h-4" />
                    <span className="ml-1">Group wise</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <Field type="radio" name="sortBy" value="member" className="w-4 h-4" />
                    <span className="ml-1">Member wise</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <Field type="radio" name="sortBy" value="area" className="w-4 h-4" />
                    <span className="ml-1 ">Area wise</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <Field type="radio" name="sortBy" value="commencement" className="w-4 h-4" />
                    <span className="ml-1  ">FUP Date wise</span>
                  </label>
                </div>

                <div className="grid grid-cols-12 gap-3 items-center p-2">
                  <div className="col-span-12 lg:col-span-2">
                    <div className={`${labelBase} mb-0.5`}>Select Groups</div>
                  </div>

                  <div className="col-span-12 lg:col-span-6 ">
                    <MultiSelectInline
                      options={groupsOptions}
                      value={values.groupsSelected}
                      onChange={(v) => setFieldValue("groupsSelected", v)}
                      placeholder="Select groups..."
                    />
                  </div>

                  <div className="col-span-12 lg:col-span-4 flex items-center lg:justify-start justify-end">
                    {values.groupsSelected.length > 0 && (
                      <div className="hidden lg:block  text-blue-600 hover:underline ml-3">
                        {values.groupsSelected.length} Groups Selected
                      </div>
                    )}
                  </div>

                  {values.groupsSelected.length > 0 && (
                    <div className="col-span-12 lg:hidden  text-center mt-2 text-blue-600 hover:underline">
                      Groups Selected: {values.groupsSelected.length}
                    </div>
                  )}
                </div>

                {/* Add in Report */}
                <h6 className="text-gray-700 font-bold mb-3 my-3 bg-gray-100 rounded p-2">Add in Report</h6>
                <div className="flex gap-6 items-center py-4 flex-wrap p-3">
                  <label className="flex items-center gap-2">
                    <Field type="checkbox" name="reportFields.address" className="w-4 h-4" />
                    <span className="ml-1  text-gray-700">Address</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <Field type="checkbox" name="reportFields.mobile" className="w-4 h-4" />
                    <span className="ml-1  text-gray-700">Mobile</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <Field type="checkbox" name="reportFields.email" className="w-4 h-4" />
                    <span className="ml-1  text-gray-700">Email</span>
                  </label>
                </div>

                <div className="flex justify-center mt-8">
                  <button type="submit" className={btnPrimary}>
                    Generate
                  </button>
                </div>

                <div className="mt-6 text-center  text-gray-500"> Premium Outstanding Report Report..</div>
              </div>
            </Form>
          )}
        </Formik>

      {/* PDF Preview Modal */}
      {showPdfModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-6 bg-black bg-opacity-40">
          <div className="bg-white rounded-md w-full max-w-6xl h-[80vh] overflow-hidden shadow-lg">
            <div className="flex items-center justify-between p-3 border-b">
              <div className="flex items-center gap-3">
                <FaRegFilePdf className="text-red-600" size={24} />
                <div className="font-semibold">Premium Outstanding Report - PDF Preview</div>
              </div>
              <div className="flex gap-2">
                <a href={pdfObjectUrl} download="Premium_Outstanding_Report.pdf" className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700">Download</a>
                <button
                  className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                  onClick={() => {
                    setShowPdfModal(false);
                    if (pdfObjectUrl) {
                      URL.revokeObjectURL(pdfObjectUrl);
                      setPdfObjectUrl(null);
                    }
                  }}
                >
                  Close
                </button>
              </div>
            </div>
            <div className="h-full">
              <iframe title="PDF Preview" src={pdfObjectUrl} width="100%" height="100%" style={{ border: 'none' }} />
            </div>
          </div>
        </div>
      )}

      <ReportLoader visible={isGenerating} message="PDF report is generating..." />
      </div>
    </div>
  );
}
